import React from 'react';

import { useTranslations } from '@unisporkal/localization';
import {
  Typography,
  TypographyComponentEnum,
  TypographyVariant,
} from '@unisporkal/alliance-ui';
import DownloadSizePreview from '../../../GenAssetAcquisitionCard/DownloadSizePreview/DownloadSizePreview';
import useSelectedIndex from '../../../../hooks/stateful/useSelectedIndex.ts';
import useOriginalSize from '../../../../shared/hooks/useOriginalSize.ts';
import useDownloadSize from '../../../../hooks/stateful/useDownloadSize.ts';
import styles from './ModifiedDownloadCompleted.module.scss';

const DEFAULT_FILE_TYPE = 'JPG';
const DEFAULT_DPI = '72';

const resolveFileType = (size) =>
  size.fileType ? size.fileType.toUpperCase() : DEFAULT_FILE_TYPE;

const toPreviewSize = (height, width, dpi) => ({
  height: height ?? '',
  width: width ?? '',
  dpi: dpi ?? DEFAULT_DPI,
});

// The entitled preshot flow stores the user's original-image size pick in
// useOriginalSize only (OriginalImage skips the useSelectedIndex sync there),
// so it wins; selectedSize covers the priced/ALC flow.
const resolveOriginalPreview = (originalSize, selectedSize) => {
  const hasOriginalPick = Boolean(
    originalSize?.pixelWidth && originalSize?.pixelHeight
  );
  const source = hasOriginalPick ? originalSize : selectedSize;
  return {
    sizeObject: toPreviewSize(
      source.pixelHeight,
      source.pixelWidth,
      source.dpi
    ),
    fileType: source.contentType,
  };
};

const ModifiedDownloadCompletedForm = () => {
  const t = useTranslations();

  const { selectedSize = {} } = useSelectedIndex();
  const { originalSize } = useOriginalSize();
  const { downloadSize } = useDownloadSize();

  // The buy card seeds downloadSize on every flow (download, license,
  // redownload) with the user's actual modified-size pick, so it wins; the
  // per-original `modifiedSize` join covers the instant before /download-sizes
  // resolves (the seed carries an empty sizeName until then, and fibo edits
  // have a null join). The final `?? {}` keeps the fileType/height/width reads
  // below null-safe if neither source has resolved yet.
  const selectedModifiedSize =
    (downloadSize?.sizeName ? downloadSize : selectedSize.modifiedSize) ?? {};

  const original = resolveOriginalPreview(originalSize, selectedSize);

  const modifiedImageSize = toPreviewSize(
    selectedModifiedSize.height,
    selectedModifiedSize.width,
    selectedModifiedSize.dpi
  );

  return (
    <div
      className={styles.modifiedDownloadCompletedForm}
      data-testid="modified-download-completed-form"
    >
      <Typography
        className={styles.para}
        variant={TypographyVariant.body.small}
        component={TypographyComponentEnum.P}
      >
        {t('the_following_was_downloaded')}
      </Typography>

      <DownloadSizePreview
        sizeObject={original.sizeObject}
        fileType={original.fileType}
      />
      <DownloadSizePreview
        sizeObject={modifiedImageSize}
        isModified
        fileType={resolveFileType(selectedModifiedSize)}
      />
    </div>
  );
};

export default ModifiedDownloadCompletedForm;
